import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';

import { convertTemperature } from '../../../lib/convert';
import ValueSpinner from './ValueSpinner';
import { COLORS } from './dataPlotConstants';

const formatTemperatureValue = (value: any, unit: any) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return null;
    }
    return convertTemperature(value, unit);
};

const StatBlock = ({ labelText, value, arrow, colorClass, unitLabel }: any) => (
    <div className='flex flex-col gap-1'>
        <span className='text-[11px] font-semibold uppercase tracking-[0.3em] text-[#626471]'>{labelText}</span>
        <span className={`flex items-center gap-1 text-sm font-semibold ${colorClass}`}>
            <span className='text-xs'>{arrow}</span>
            {value !== null ? `${value}${unitLabel}` : '-'}
        </span>
    </div>
);

function TemperatureCard({
    title,
    label,
    accent,
    accentLight,
    accentBg, // retained for compatibility though not used directly
    values,
    type,
    tempUnit,
    isMobile,
    isLoading,
    buildCardStyle,
    baseTitleStyle,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'Overview' });
    const unitLabel = tempUnit === 'fahrenheit' ? '°F' : '°C';
    const formatValue = (field: any, invalidValue?: any) => {
        const raw = values?.[field];
        if (typeof raw !== 'number' || (typeof invalidValue === 'number' && raw === invalidValue)) {
            return null;
        }
        return Math.round(formatTemperatureValue(raw, tempUnit));
    };

    const average = formatValue('avg');
    const max = formatValue('max', 0);
    const min = formatValue('min', 100);

    const isFail = type === 'fail';
    const gradient = isFail ? 'from-[#FFD7DD] via-white to-white' : 'from-[#DDEBFF] via-white to-white';
    const valueColor = isFail ? 'text-[#E04952]' : 'text-[#2F7AE5]';
    const maxColor = isFail ? 'text-[#FF6067]' : 'text-[#3B82F6]';
    const minColor = isFail ? 'text-[#F36E7A]' : 'text-[#2563EB]';

    const accentColor = isFail ? '#FF5959' : '#3F8EE8';
    const minArrowColor = isFail ? '#FF9090' : '#ECF4FD';

    const stripTempLabel = (text: any) => {
        if (!isMobile || typeof text !== 'string') return text;
        const cleaned = text.replace(/temperature|temp/gi, '').trim();
        return cleaned.length > 0 ? cleaned : text;
    };

    const maxLabel = stripTempLabel(t('maxTemp'));
    const minLabel = stripTempLabel(t('minTemp'));

    const cardBody = (
        <div className={`temp-card-mobile ${isFail ? 'temp-card-mobile--fail' : 'temp-card-mobile--pass'}`}>
            <div className='temp-card-mobile__content' style={{ color: accentColor }}>
                <div className='temp-card-mobile__header'>
                    <span>{label}</span>
                    <span className='temp-card-mobile__unit temp-card-mobile__unit--absolute'>{unitLabel}</span>
                </div>
                {isLoading ? (
                    // minHeight holds the 58px line box so the spinner keeps the card's height.
                    <p className='temp-card-mobile__value' style={{ minHeight: '58px' }}>
                        <ValueSpinner color={accentColor} size={34} boxHeight={58} />
                    </p>
                ) : (
                    <p className='temp-card-mobile__value'>{average ?? '-'}</p>
                )}
            </div>
            <div className='temp-card-mobile__wave' />
            <div className='temp-card-mobile__footer'>
                <div className='temp-card-mobile__stat-row'>
                    {maxLabel}
                    <span className='temp-card-mobile__stat-value' style={{ color: accentColor }}>
                        {isLoading ? (
                            <ValueSpinner color={accentColor} size={14} />
                        ) : (
                            `▲ ${max !== null ? `${max}${unitLabel}` : '-'}`
                        )}
                    </span>
                </div>
                <div className='temp-card-mobile__stat-row'>
                    {minLabel}
                    <span className='temp-card-mobile__stat-value'>
                        {isLoading ? (
                            <ValueSpinner color={accentColor} size={14} />
                        ) : (
                            <>
                                <span style={{ color: minArrowColor }}>▼</span>{' '}
                                <span style={{ color: accentColor }}>{min !== null ? `${min}${unitLabel}` : '-'}</span>
                            </>
                        )}
                    </span>
                </div>
            </div>
        </div>
    );

    if (isMobile) {
        return cardBody;
    }

    const innerCardStyle = {
        width: '80%',
        height: '70%',
        border: `2px ${accent} solid`,
        borderRadius: '15px',
        position: 'relative',
        backgroundColor: accentBg || COLORS.white,
        overflow: 'hidden',
        display: 'flex',
        flexFlow: 'column nowrap',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: '10px 0 20px',
        boxSizing: 'border-box',
    };

    const domeStyle = {
        width: '125%',
        height: '50%',
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transform: 'translate(-50%, 0)',
        borderRadius: '100% 100% 15px 15px',
        backgroundColor: COLORS.white,
    };

    const labelStyle = {
        height: '20px',
        fontSize: '25px',
        textAlign: 'center',
        color: accent,
        fontFamily: 'Inter, Roboto, sans-serif',
        fontWeight: 'bold',
        marginTop: '5px',
        zIndex: 1,
    };

    const unitStyle = {
        height: '10px',
        fontSize: '35px',
        textAlign: 'right',
        color: accent,
        fontFamily: 'Roboto, sans-serif',
        fontWeight: 'bold',
        zIndex: 1,
        marginRight: '10%',
        alignSelf: 'flex-end',
    };

    const valueStyle = {
        height: '80px',
        fontSize: '90px',
        textAlign: 'center',
        color: accent,
        fontFamily: 'Roboto, sans-serif',
        fontWeight: 'bold',
        zIndex: 1,
        marginTop: '-10px',
        lineHeight: 1,
    };

    const statRowBase = {
        fontSize: '15px',
        textAlign: 'center',
        color: COLORS.textSecondary,
        fontFamily: 'Inter, Roboto, sans-serif',
        zIndex: 1,
        display: 'flex',
        flexFlow: 'row nowrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
    };

    const maxRowStyle = { ...statRowBase, marginTop: '20px' };
    const minRowStyle = { ...statRowBase };

    const buildArrow = (direction: any, color: any) => (
        <svg width='20' height='20' viewBox='-100 0 400 550' style={{ zIndex: 1 }}>
            <polygon
                strokeLinejoin='round'
                points={direction === 'up' ? '100,200 0,400 200,400' : '0,200 100,400 200,200'}
                style={{ fill: color, stroke: color, strokeWidth: '100px' }}
            />
        </svg>
    );

    const formatStatValue = (value: any) => (value !== null ? `${value}${unitLabel}` : '-');

    // valueStyle is a plain block, so centre the placeholder explicitly inside its 80px box.
    const loadingValueStyle = { ...valueStyle, display: 'flex', justifyContent: 'center', alignItems: 'center' };

    return (
        <div
            className={`card temp-card ${isFail ? 'temp-card--fail' : 'temp-card--pass'}`}
            style={buildCardStyle('small', { alignItems: 'center' })}
        >
            <div style={baseTitleStyle}>{title}</div>
            <div style={innerCardStyle as any}>
                <div style={domeStyle as any} />
                <div style={labelStyle as any}>{label}</div>
                <div style={unitStyle as any}>{unitLabel}</div>
                {isLoading ? (
                    <div style={loadingValueStyle as any}>
                        <ValueSpinner color={accent} size={52} boxHeight={80} />
                    </div>
                ) : (
                    <div style={valueStyle as any}>{average ?? '-'}</div>
                )}
                <div style={maxRowStyle as any}>
                    {t('maxTemp')}
                    {buildArrow('up', accent)}
                    {isLoading ? (
                        <ValueSpinner color={accent} size={16} />
                    ) : (
                        <span style={{ color: accent, fontWeight: 600 }}>{formatStatValue(max)}</span>
                    )}
                </div>
                <hr
                    style={{
                        height: '1px',
                        width: '80%',
                        zIndex: 1,
                        margin: '6px auto',
                        backgroundColor: accent,
                        border: 'none',
                    }}
                />
                <div style={minRowStyle as any}>
                    {t('minTemp')}
                    {buildArrow('down', accentLight)}
                    {isLoading ? (
                        <ValueSpinner color={accentLight} size={16} />
                    ) : (
                        <span style={{ color: accentLight, fontWeight: 600 }}>{formatStatValue(min)}</span>
                    )}
                </div>
            </div>
        </div>
    );
}

TemperatureCard.propTypes = {
    title: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    accent: PropTypes.string.isRequired,
    accentLight: PropTypes.string.isRequired,
    accentBg: PropTypes.string,
    values: PropTypes.shape({
        avg: PropTypes.number,
        max: PropTypes.number,
        min: PropTypes.number,
    }),
    type: PropTypes.oneOf(['pass', 'fail']).isRequired,
    tempUnit: PropTypes.string.isRequired,
    isMobile: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool,
    buildCardStyle: PropTypes.func.isRequired,
    baseTitleStyle: PropTypes.object.isRequired,
};

export default TemperatureCard;
